import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter } from "@astryxdesign/core/Layout";
import { HStack } from "@astryxdesign/core/HStack";
import { VStack } from "@astryxdesign/core/VStack";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { Token } from "@astryxdesign/core/Token";
import { Badge } from "@astryxdesign/core/Badge";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Video } from "lucide-react";
import { api } from "../../lib/api";
import { RetryBanner } from "../../components/RetryBanner";

interface RunningStreamsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Placeholder row count while the list loads — matches PendingRequestsDialog's
// own reasoning: a guess at the eventual count would promise a number that
// isn't real yet, so this is deliberately just "a couple of rows are coming".
const LOADING_ROW_COUNT = 3;

/** How long a stream has been up, in the coarsest unit that is still useful. */
function since(startedAt: string): string {
  const ms = Date.now() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/**
 * Lists the streams currently counted against the quota.
 *
 * This is what makes a shared limit comprehensible. Without it, "no free
 * stream slots" is a wall an operator cannot see through: they do not know
 * what is using the capacity, whether anybody is actually watching it, or
 * whether the thing to do is wait, ask, or raise a limit.
 *
 * Server-scoped, not filtered here: the endpoint returns only cameras this
 * caller may see, and one running elsewhere is deliberately absent rather than
 * shown anonymously.
 */
export function RunningStreamsDialog({ isOpen, onOpenChange }: RunningStreamsDialogProps) {
  const {
    data: streams,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["streams-active"],
    queryFn: api.streams.active,
    enabled: isOpen,
    // A running-streams list that goes stale while somebody reads it is worse
    // than none: they would decide what to stop based on what was true when
    // the dialog opened.
    refetchInterval: 5_000,
  });

  const running = streams ?? [];

  let body: ReactNode;
  let footerCount: ReactNode = null;

  if (isLoading) {
    body = (
      <VStack gap={2}>
        {Array.from({ length: LOADING_ROW_COUNT }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed-count static placeholders, never reordered
          <Skeleton key={i} index={i} height={44} />
        ))}
      </VStack>
    );
  } else if (error) {
    // Previously rendered the exact same "Nothing is streaming" EmptyState as
    // a genuinely idle site, with the footer's own "0 running" token
    // corroborating the lie — directly contradicting the {active}/{limit}
    // badge the operator just clicked to get here.
    body = (
      <RetryBanner
        title="Couldn't load running streams"
        description={error instanceof Error ? error.message : "Unknown error"}
        onRetry={() => void refetch()}
        isRetrying={isRefetching}
      />
    );
  } else {
    footerCount = <Token label={`${running.length} running`} size="sm" />;
    body =
      running.length === 0 ? (
        <EmptyState
          icon={<Video />}
          title="Nothing is streaming"
          description="No camera you can see is being pulled at the moment."
        />
      ) : (
        <VStack gap={2}>
          {running.map((s) => (
            <HStack key={s.camera_id} gap={2} vAlign="center" justify="between" wrap="wrap">
              <VStack gap={0} style={{ minWidth: 0 }}>
                <Text weight="medium" maxLines={1}>
                  {s.name || "A camera at another site"}
                </Text>
                <Text type="supporting" color="secondary">
                  running for {since(s.started_at)}
                </Text>
              </VStack>
              {/* Badge, not Token: this is a count. The zero case is the
                  interesting one — a stream nobody is watching is the
                  first thing eviction takes, so it is worth seeing. */}
              <Badge
                variant={s.viewers === 0 ? "neutral" : "green"}
                label={s.viewers === 0 ? "nobody watching" : `${s.viewers} watching`}
              />
            </HStack>
          ))}
        </VStack>
      );
  }

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="info" width={520}>
      <Layout
        header={
          <DialogHeader
            title="Running streams"
            subtitle="Cameras being pulled right now. Each one counts against this site's limit and the system-wide one."
            onOpenChange={onOpenChange}
          />
        }
        content={<LayoutContent>{body}</LayoutContent>}
        footer={
          <LayoutFooter>
            <HStack gap={2} justify={footerCount ? "between" : "end"} vAlign="center">
              {/* Absent while loading or on error, rather than showing a
                  "0 running" that would assert a specific, possibly false
                  count — see footerCount's own assignment above. justify
                  falls back to "end" so Close doesn't drift to the left
                  with nothing on the other side of "between" to balance it. */}
              {footerCount}
              <Button label="Close" variant="ghost" onClick={() => onOpenChange(false)} />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
